import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Filters,
} from 'hooks/store/restricted-products/use-restriction-event-search-params';
import { RestrictionEventAction } from 'contracts/restricted-products/restricted-products';
import { Form, FormField } from 'components/ui/form';
import { InputGs, ComboboxGs, DateRangePickerGs } from 'components/ui/Forms';
import { Button as ButtonGs } from 'components/ui/ButtonGs';

import { formSchema, type FormSchema } from './form-schema';
import { createFilterParams } from './utils';

type Props = {
  onSubmitFilters: (filters: Filters) => void
  onResetFilters: () => void
}

const justificationStatusOptions = [
  { value: 'justified', name: 'Justificado' },
  { value: 'non-justified', name: 'Pendente de justificativa' },
];

const actionOptions: { value: RestrictionEventAction, name: string }[] = [
  { value: 'block', name: 'Bloqueio' },
  { value: 'unblock', name: 'Desbloqueio' },
  { value: 'updateQuantity', name: 'Alteração de quantidade' },
  { value: 'saleMovement', name: 'Movimento de venda' },
];

const sortingBiasOptions = [
  { value: 'product', name: 'Agrupado por material, planta e lote' },
  { value: 'creation-date', name: 'Data do evento' },
];

export const RestrictionEventsFilter = ({ onSubmitFilters, onResetFilters }: Props) => {
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const resetForm = React.useCallback(() => {
    form.reset({
      batch: '',
      material: '',
      materialDescription: '',
      plant: '',
      action: undefined,
      createdAt: undefined,
      justificationStatus: undefined,
      sortingBias: 'creation-date'
    }, { keepValues: false });
    onResetFilters();
  }, [form, onResetFilters]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    const filters = createFilterParams(formData);
    onSubmitFilters(filters);
  }, [onSubmitFilters]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
        <div className={'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'}>
          <FormField
            control={form.control}
            name={'material'}
            render={({ field }) => (
              <InputGs {...field} type={'text'} label={'Código do material'} />
            )}
          />
          <FormField
            control={form.control}
            name={'plant'}
            render={({ field }) => (
              <InputGs {...field} type={'text'} label={'Planta'} />
            )}
          />
          <FormField
            control={form.control}
            name={'materialDescription'}
            render={({ field }) => (
              <InputGs {...field} type={'text'} label={'Descrição do material'} />
            )}
          />
          <FormField
            control={form.control}
            name={'batch'}
            render={({ field }) => (
              <InputGs {...field} type={'text'} label={'Lote'} />
            )}
          />
          <FormField
            control={form.control}
            name={'createdAt'}
            render={({ field }) => (
              <DateRangePickerGs
                selected={{
                  from: field.value?.from,
                  to: field.value?.to,
                }}
                onSelect={field.onChange}
                label={'Data do evento'}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'action'}
            render={({ field }) => (
              <ComboboxGs
                label={'Ação'}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={actionOptions}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'justificationStatus'}
            render={({ field }) => (
              <ComboboxGs
                label={'Status da justificativa'}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={justificationStatusOptions}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'sortingBias'}
            render={({ field }) => (
              <ComboboxGs
                label={'Ordem'}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={sortingBiasOptions}
              />
            )}
          />
        </div>
        <div className={'flex gap-4 mt-6'}>
          <ButtonGs.Root
            type={'button'}
            variant={'secondary'}
            size={'sm'}
            onClick={resetForm}
          >
            Limpar filtros
          </ButtonGs.Root>
          <ButtonGs.Root type={'submit'} size={'sm'}>
            Filtrar
          </ButtonGs.Root>
        </div>
      </form>
    </Form>
  );
};
